import { Controller } from "@hotwired/stimulus"

// The portfolio's Assets | History packet: the tab row in its header band,
// and the pairing between the donut's arcs and the asset rows beneath it.
//
// Tabs: one panel shows at a time, the chosen one kept in the URL hash so a
// reload or the back button lands on the same tab. Without JavaScript both
// panels render one under the other, each with its own heading.
//
// Pairing: hovering a row lifts its arc and fades the others; hovering an arc
// marks its row. On a touch screen a tap on a row does the same, and a tap
// anywhere else clears it. The donut is decoration for the rows, which carry
// every number in text, so nothing here is needed to read the page.
export default class extends Controller {
  static targets = ["tab", "panel", "bandLabel", "arc", "row"]

  connect() {
    this.show(location.hash === "#history" ? "history" : "assets")
    // Which kind of pointer last touched the packet decides whether a click
    // is a tap (toggle) or the end of a mouse hover (already handled).
    this.onPointerDown = (event) => { this.lastPointer = event.pointerType }
    this.element.addEventListener("pointerdown", this.onPointerDown)
    this.onDocumentClick = (event) => {
      if (this.lifted && !event.target.closest("[data-asset]")) this.highlight(null)
    }
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.onPointerDown)
    document.removeEventListener("click", this.onDocumentClick)
  }

  // --- tabs ---------------------------------------------------------------

  select(event) {
    const tab = event.params.tab
    this.show(tab)
    const url = tab === "assets" ? location.pathname + location.search : "#history"
    history.replaceState(null, "", url)
  }

  arrow(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    event.preventDefault()
    const tabs = this.tabTargets
    const step = event.key === "ArrowRight" ? 1 : tabs.length - 1
    const next = tabs[(tabs.indexOf(event.currentTarget) + step) % tabs.length]
    next.focus()
    next.click()
  }

  show(tab) {
    this.tabTargets.forEach((button) => {
      const selected = button.dataset.portfolioTabParam === tab
      button.setAttribute("aria-selected", String(selected))
      button.tabIndex = selected ? 0 : -1
    })
    this.panelTargets.forEach((panel) => { panel.hidden = panel.dataset.tab !== tab })
    this.bandLabelTargets.forEach((label) => { label.hidden = label.dataset.tab !== tab })
  }

  // --- donut <-> rows -----------------------------------------------------

  lift(event) {
    if (event.pointerType !== "mouse") return
    this.highlight(event.currentTarget.dataset.asset)
  }

  drop(event) {
    if (event.pointerType !== "mouse") return
    this.highlight(null)
  }

  toggle(event) {
    if (this.lastPointer !== "touch") return
    const asset = event.currentTarget.dataset.asset
    this.highlight(this.lifted === asset ? null : asset)
  }

  highlight(asset) {
    this.lifted = asset
    for (const element of [...this.arcTargets, ...this.rowTargets]) {
      const mine = Boolean(asset) && element.dataset.asset === asset
      element.classList.toggle("is-lifted", mine)
      element.classList.toggle("is-dimmed", Boolean(asset) && !mine)
    }
  }
}
